import { layers, namedFlavor } from "@protomaps/basemaps";
import type { LayerSpecification, SourceSpecification } from "maplibre-gl";
import * as maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import { mapLocale } from "../../utils/map-locale";
import { supportsWebGl2 } from "../../utils/webgl";

// v6 is ESM-only; import.meta.url doesn't reliably resolve to the worker
// file inside rspack's module graph, so the worker URL needs setting
// explicitly — see "setWorkerUrl() is bundler-only":
// https://maplibre.org/maplibre-gl-js/docs/guides/v5-to-v6-migration-guide/#setworkerurl-is-bundler-only
// The worker and its shared.mjs sibling are copied to a version-namespaced
// directory (rsbuild.config.ts) rather than through rspack's default
// content-hashed asset pipeline — see that config for why.
maplibregl.setWorkerUrl(
  `/inc/assets/maplibre-gl-${process.env.MAPLIBRE_GL_VERSION}/maplibre-gl-worker.mjs`,
);

export const initProfileMap = () => {
  const mapProfile = document.getElementById("map-profile") as HTMLDivElement;

  if (!mapProfile) {
    return;
  }

  // setRTLTextPlugin() returns a Promise that rejects if the remote plugin
  // script fails to load (e.g. api.mapbox.com being unreachable for some
  // users/regions) - `void` alone doesn't catch that, so it was surfacing as
  // an unhandled rejection (BABYSITS-FRONTEND-11P). Non-fatal either way: the
  // map still works, RTL-script labels just won't render for that session.
  maplibregl
    .setRTLTextPlugin(
      "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js",
      true,
    )
    .catch(() => {});

  const setMarkerForUserLocation = (map: maplibregl.Map) => {
    /* Set user home address on the map */
    const forUserLat = mapProfile.dataset.forUserLat as string;
    const forUserLng = mapProfile.dataset.forUserLng as string;

    const lat = forUserLat.trim();
    const lng = forUserLng.trim();

    if (!lng || !lat) {
      return;
    }

    // create a DOM element for the marker
    const markerLocation = document.createElement("div");
    markerLocation.className = "marker-for-user-location";
    markerLocation.style.backgroundImage =
      "url(https://cdn.babysits.com/global/icons/maps/home.svg)";
    markerLocation.style.backgroundSize = "cover";
    markerLocation.style.width = `${36}px`;
    markerLocation.style.height = `${36}px`;
    markerLocation.style.zIndex = "1";
    markerLocation.style.cursor = "pointer";

    const marker = new maplibregl.Marker({ element: markerLocation })
      .setLngLat([parseFloat(lng), parseFloat(lat)])
      .addTo(map);

    const homeTooltip = mapProfile.dataset.homeTooltip?.trim();

    if (homeTooltip) {
      marker.setPopup(
        new maplibregl.Popup({ offset: 24, closeButton: false }).setText(homeTooltip),
      );
    }
  };

  const initialize_map_profile = () => {
    if (!supportsWebGl2()) {
      // maplibre-gl throws GPUInitializationError without WebGL2; hide the map instead.
      console.info("Map disabled: this browser has no WebGL2 support.");
      mapProfile.classList.add("d-none");

      return;
    }

    const spinner = mapProfile.querySelector(".spinner") as HTMLDivElement;
    spinner.style.display = "block";

    /* Bots can affect the mapbox billing by 60%. So we do not load the map for these bots anymore. */
    const listOfBots = [""];

    if (listOfBots.includes(navigator.userAgent)) {
      return;
    }

    const publicLat = mapProfile.dataset.userPublicLat as string;
    const publicLng = mapProfile.dataset.userPublicLng as string;

    const userPublicLat = parseFloat(publicLat.trim());
    const userPublicLng = parseFloat(publicLng.trim());

    const mediaQueryObj = window.matchMedia("(prefers-color-scheme: dark)");
    const isDarkMode = mediaQueryObj.matches;
    const activeMode: "dark" | "light" = isDarkMode ? "dark" : "light";

    // Initialize PMTiles protocol handler before creating the map
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    // Get language from html element
    const htmlLang = document.documentElement.lang; // "nl-NL"

    // Extract just the language code (before the hyphen)
    const langCode = htmlLang.split("-")[0]; // "nl"

    const map = new maplibregl.Map({
      container: "map-profile",
      center: [userPublicLng, userPublicLat],
      zoom: 13,
      scrollZoom: false,
      minZoom: 9,
      maxZoom: 15,
      dragRotate: false,
      validateStyle: false, // Disable style validation for faster map load
      locale: mapLocale(mapProfile),
      style: {
        version: 8,
        glyphs: "https://maps.babysits.com/fonts/{fontstack}/{range}.pbf",
        sprite: `https://maps.babysits.com/sprites/v4/${activeMode}`,
        sources: {
          babysits_maps: {
            type: "vector",
            url: "pmtiles://https://maps.babysits.com/20251101.pmtiles",
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: layers("babysits_maps", namedFlavor(activeMode), {
          lang: langCode,
        }) as LayerSpecification[],
      },
    });
    map.touchZoomRotate.disableRotation();

    map.addControl(
      new maplibregl.NavigationControl({
        showCompass: false,
      }),
    );

    map.addControl(
      new maplibregl.GeolocateControl({
        fitBoundsOptions: {
          maxZoom: 13,
        },
      }),
    );

    map.on("load", () => {
      /* https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js */
      const createGeoJSONCircle = (
        lat: number,
        lng: number,
        radiusInKm: number,
        points?: number,
      ) => {
        if (!points) points = 64;

        const coords = { latitude: lat, longitude: lng };

        const km = radiusInKm;

        const ret = [];
        const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
        const distanceY = km / 110.574;

        let theta: number;
        let x: number;
        let y: number;

        for (let i = 0; i < points; i++) {
          theta = (i / points) * (2 * Math.PI);
          x = distanceX * Math.cos(theta);
          y = distanceY * Math.sin(theta);

          ret.push([coords.longitude + x, coords.latitude + y]);
        }
        ret.push(ret[0]);

        return {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Polygon",
                  coordinates: [ret],
                },
              },
            ],
          },
        } as SourceSpecification;
      };

      /**
       * The radiusinKm depends on the geohash length: https://en.wikipedia.org/wiki/Geohash
       */
      map.addSource("polygon", createGeoJSONCircle(userPublicLat, userPublicLng, 0.5));

      map.addLayer({
        id: "polygon",
        type: "fill",
        source: "polygon",
        layout: {},
        paint: {
          "fill-color": "rgba(247, 161, 188, 0.4)",
          "fill-outline-color": "#CB4D74",
        },
      });

      setMarkerForUserLocation(map);

      spinner.style.display = "none";
    });
  };

  function handleIntersect(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0) {
        initialize_map_profile();
        observer.unobserve(entry.target);
      }
    });
  }

  const observer = new IntersectionObserver(handleIntersect);

  observer.observe(mapProfile);
};
